import { ShieldCheck, FileText, Building2, Compass, Ban, Scale } from "lucide-react";

/**
 * Important Facts—single source of truth shared by:
 *   - /important-facts (Disclosures page)
 *   - the side-drawer wired into the vestibule acknowledgement step
 */
export const IMPORTANT_FACTS = [
  {
    id: "fee-model",
    eyebrow: "01 · Revenue model",
    title: "We are paid by management fees.",
    icon: ShieldCheck,
    body: [
      "Steck Aviation Management is a management services company. Our revenue is the management fee we are paid by aircraft owners under a written Aircraft Management Agreement.",
      "We do not earn commissions on aircraft sales. We do not mark up maintenance. We do not profit from fuel uplift, parts, pilot time, or any other operating line item.",
      "Every dollar an owner spends on the operation of the aircraft passes through to the actual vendor or service provider. Our compensation is the management fee—disclosed, fixed, and on paper.",
    ],
  },
  {
    id: "pro-pilots",
    eyebrow: "02 · Crew sourcing",
    title: "We source professional pilots—on insurance-acceptable terms.",
    icon: FileText,
    body: [
      "Aircraft owners flying complex, turbine, or jet equipment are typically required by their insurance underwriters to crew the aircraft with professional pilots meeting specific type-rating, hours-in-type, and recurrent training thresholds.",
      "When we use the term \"professional pilots\" we mean pilots who satisfy those underwriting standards. This is a language and risk question—not a Part 135 commercial pilot designation. The owner-operator relationship under Part 91 remains intact.",
      "We coordinate the sourcing, vetting, and onboarding of pilots who meet the owner's policy. Pilots are paid directly by the owner or through a structure the owner and their counsel select.",
    ],
  },
  {
    id: "part-135",
    eyebrow: "03 · Part 135 certification",
    title: "Pre-stage today. A defined post-stage vision.",
    icon: Compass,
    body: [
      "Steck Aviation Management presently operates under Part 91—owner-operated, non-commercial. We do not offer charter, on-demand flights, or any service that would require a Part 135 air-carrier certificate.",
      "We are pre-Part-135. Once certified, we envision the future of Steck Aviation Management as a full-scale aviation services platform—FBO operations, maintenance, avionics, and a meaningful nationwide presence. Until that certificate is in hand, we operate exclusively within the management-services scope described above.",
      "This is the line, and we are explicit about which side of it we are on at any given time.",
    ],
  },
  {
    id: "not-fractional",
    eyebrow: "04 · What this is not",
    title: "This is not fractional or shared ownership.",
    icon: Building2,
    body: [
      "Steck Aviation Management does not sell, offer, or facilitate any fractional, jet-card, or partial-ownership program. There is no shared interest. There is no membership tier. There is no time bank.",
      "If you engage Steck, you are the owner of one aircraft and we manage that aircraft on your behalf. That is the entire scope.",
    ],
  },
  {
    id: "operational-control",
    eyebrow: "05 · Operational control",
    title: "We do not hold operational control.",
    icon: Ban,
    body: [
      "Operational control of the aircraft remains, at all times, with the owner. The owner decides when, where, and whether the aircraft flies. The owner selects the pilots, approves the flights, and bears the operational responsibility.",
      "Steck Aviation Management is a service provider, not a principal. We coordinate; we do not direct. We make recommendations; the owner decides. Our agreements are explicit on this point because the FAA, the IRS, and the insurance market are all explicit on this point.",
      "The corollary is that nothing we offer is mandatory. An owner who engages our management services retains complete discretion over which services to use, which vendors to retain, and how the operation runs.",
    ],
  },
  {
    id: "regulatory-overlap",
    eyebrow: "06 · Where laws meet",
    title: "FAA, SEC / FINRA, insurance—overlapping, not interchangeable.",
    icon: Scale,
    body: [
      "Aircraft ownership sits at the intersection of aviation regulation (FAA / 14 CFR Part 91, Part 135), tax structure, securities considerations where multi-owner or syndicated arrangements are involved, and insurance underwriting. These regimes overlap; they are not interchangeable; and conflating them is the most common way owners get themselves into trouble.",
      "Nothing on this site is legal, tax, insurance, or investment advice. Steck Aviation Management is not a law firm, a registered broker-dealer, an investment adviser, or an insurance carrier. We coordinate with the owner's chosen counsel, accountants, and brokers—we do not replace them.",
    ],
  },
];
